import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Avatar,
  Typography,
  Divider,
  message,
  Breadcrumb,
  Modal,
  Space,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  LockOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { supabase } from '../services/supabase';
import './Profile.css';
import { logger } from '../utils/logger';
import { storageHelper } from '../utils/storageHelper';

const { Title, Text } = Typography;

interface UserData {
  id: string;
  username: string;
  name: string;
  phone_number?: string;
  company?: { name: string };
  branch?: { branch_name: string };
  is_admin: boolean;
  has_admin_role: boolean;
}

const Profile: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // 현재 로그인한 사용자 정보 가져오기
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        message.error('로그인 정보를 찾을 수 없습니다.');
        return;
      }

      // DB에서 최신 사용자 정보 가져오기
      const { data: dbUserData, error } = await supabase
        .from('users')
        .select(`
          *,
          company:companies(name),
          branch:branches(branch_name)
        `)
        .eq('auth_id', user.id)
        .single();

      if (error || !dbUserData) {
        // localStorage에서 가져오기
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const parsedData = JSON.parse(storedUserData);
          setUserData(parsedData);
          
          form.setFieldsValue({
            username: parsedData.username,
            name: parsedData.name,
            phone_number: parsedData.phone_number,
            company: parsedData.company?.name,
            branch: parsedData.branch?.branch_name,
          });
        }
        return;
      }

      // 상태 업데이트
      const formattedUserData: UserData = {
        id: dbUserData.id,
        username: dbUserData.username,
        name: dbUserData.name,
        phone_number: dbUserData.phone_number || undefined,
        company: dbUserData.company || undefined,
        branch: dbUserData.branch || undefined,
        is_admin: dbUserData.is_admin || false,
        has_admin_role: dbUserData.has_admin_role || false,
      };
      
      setUserData(formattedUserData);
      storageHelper.saveUserData(formattedUserData);
      
      // 폼에 데이터 설정
      form.setFieldsValue({
        username: dbUserData.username,
        name: dbUserData.name,
        phone_number: dbUserData.phone_number,
        company: dbUserData.company?.name,
        branch: dbUserData.branch?.branch_name,
      });
    } catch (error) {
      logger.error('Error loading user data:', error);
      message.error('사용자 정보를 불러오는데 실패했습니다.');
    }
  };

  const handleSave = async () => {
    try {
      if (!userData) {
        message.error('사용자 정보를 찾을 수 없습니다.');
        return;
      }

      setLoading(true);
      const values = await form.validateFields();
      
      // 사용자 정보 업데이트
      const { error } = await supabase
        .from('users')
        .update({
          name: values.name,
          phone_number: values.phone_number,
        })
        .eq('id', userData.id);

      if (error) {
        logger.error('Update error:', error);
        message.error(`정보 수정에 실패했습니다: ${error.message}`);
        return;
      }

      // localStorage 업데이트
      const updatedUserData: UserData = {
        ...userData,
        name: values.name,
        phone_number: values.phone_number || undefined,
      };
      storageHelper.saveUserData(updatedUserData);
      setUserData(updatedUserData);

      message.success('정보가 수정되었습니다.');
      setIsEditing(false);
    } catch (error) {
      logger.error('Save error:', error);
      message.error('정보 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      // 폼 검증 먼저 수행
      const values = await passwordForm.validateFields();
      
      if (!userData) {
        message.error('사용자 정보를 찾을 수 없습니다.');
        return;
      }

      setLoading(true);

      // 현재 비밀번호 확인을 위해 재로그인 시도
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: `${userData.username}@tbm.app`,
        password: values.currentPassword,
      });

      if (signInError) {
        // 현재 비밀번호가 틀린 경우 폼 에러로 표시
        passwordForm.setFields([
          {
            name: 'currentPassword',
            errors: ['현재 비밀번호가 올바르지 않습니다.'],
          },
        ]);
        setLoading(false);
        return;
      }

      // 비밀번호 변경
      const { error: updateError } = await supabase.auth.updateUser({
        password: values.newPassword
      });

      if (updateError) {
        // 422 에러나 기타 에러 처리
        if (updateError.message.includes('should be different') || updateError.message.includes('same as the old')) {
          passwordForm.setFields([
            {
              name: 'newPassword',
              errors: ['새 비밀번호는 현재 비밀번호와 달라야 합니다.'],
            },
          ]);
        } else if (updateError.message.includes('weak') || updateError.message.includes('6 characters')) {
          passwordForm.setFields([
            {
              name: 'newPassword',
              errors: ['비밀번호는 최소 6자 이상이어야 합니다.'],
            },
          ]);
        } else {
          message.error('비밀번호 변경에 실패했습니다.');
        }
        setLoading(false);
        return;
      }

      message.success('비밀번호가 변경되었습니다.');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      // 폼 검증 실패는 catch로 오지 않음 (validateFields가 Promise reject)
      logger.error('Password change error:', error);
      setLoading(false);
    }
  };

  const getRoleText = () => {
    if (userData?.is_admin) return '슈퍼관리자';
    if (userData?.has_admin_role) return '관리자';
    return '일반사용자';
  };

  return (
    <div className="profile-page">
      <Breadcrumb className="breadcrumb">
        <Breadcrumb.Item>홈</Breadcrumb.Item>
        <Breadcrumb.Item>내 정보</Breadcrumb.Item>
      </Breadcrumb>

      <div className="page-header">
        <h1 className="page-title">내 정보</h1>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card className="profile-card">
            <div className="profile-avatar-section">
              <Avatar size={120} icon={<UserOutlined />} />
              <Title level={4} style={{ marginTop: 16 }}>
                {userData?.name}
              </Title>
              <Text type="secondary">{getRoleText()}</Text>
            </div>
            <Divider />
            <div className="profile-info">
              <p>
                <strong>아이디:</strong> {userData?.username}
              </p>
              <p>
                <strong>회사:</strong> {userData?.company?.name || '-'}
              </p>
              <p>
                <strong>지점:</strong> {userData?.branch?.branch_name || '-'}
              </p>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card
            title="기본 정보"
            extra={
              !isEditing ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setIsEditing(true)}
                >
                  수정
                </Button>
              ) : (
                <Space.Compact>
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      form.resetFields();
                      loadUserData();
                    }}
                  >
                    취소
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loading}
                  >
                    저장
                  </Button>
                </Space.Compact>
              )
            }
          >
            <Form
              form={form}
              layout="vertical"
              disabled={!isEditing}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="아이디" name="username">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="이름"
                    name="name"
                    rules={[{ required: true, message: '이름을 입력해주세요' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="전화번호" name="phone_number">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="회사" name="company">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="지점" name="branch">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          <Card
            title="보안 설정"
            style={{ marginTop: 24 }}
          >
            <Button
              icon={<LockOutlined />}
              onClick={() => setPasswordModalVisible(true)}
            >
              비밀번호 변경
            </Button>
          </Card>
        </Col>
      </Row>

      <Modal
        title="비밀번호 변경"
        open={passwordModalVisible}
        onOk={handlePasswordChange}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        okText="변경"
        cancelText="취소"
        confirmLoading={loading}
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            label="현재 비밀번호"
            name="currentPassword"
            rules={[{ required: true, message: '현재 비밀번호를 입력해주세요' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label="새 비밀번호"
            name="newPassword"
            rules={[
              { required: true, message: '새 비밀번호를 입력해주세요' },
              { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다' },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label="새 비밀번호 확인"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '새 비밀번호를 다시 입력해주세요' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('비밀번호가 일치하지 않습니다'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;